import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import EditIcon from "@mui/icons-material/Edit";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { MdOutlineQueryStats } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactSelect, { components } from "react-select";
import { toast } from "react-toastify";
import { DeleteIcon } from "../../assets/Icons";
import { H1Header } from "../../components";
import BotBlogrButton from "../../components/dashboard/BotBlogrButton";
import GlobalTextField from "../../components/post_editor/FeaturesTemplate/FeaturesFields/GlobalTextField";
import { trackPlatformUsed } from "../../components/trackers";
import { setCredentials } from "../../redux/slices/authSlice";
import { setCompetitorAnalysis } from "../../redux/slices/competitorSlice";
import {
  useCreatePostEditorBlogMutation,
  useDeleteSelectedPostEditorBlogMutation,
  useDeleteSinglePostEditorBlogMutation,
  useGetAllPostEditorBlogQuery,
  useGetCompetitorAnalysisMutation,
  useLinkSinglePostEditorToWebsiteMutation,
} from "../../redux/slices/postEditorBlogSlice";
import { useGetAllProjectsQuery } from "../../redux/slices/projectApiSlice";
import { dispatch } from "../../redux/store";
import { DefaultPostEditor } from "./helper";
import { CircularGauge } from "./seoAnalyzer/helper";
// Pour supprimer le séparateur
const NoSeparator = () => null;

const AllPostEditor = () => {
  const { t } = useTranslation();

  // Website init
  const currentProject = useSelector((state) => state?.project);
  const isInitialized = currentProject?.isInitialized;
  const website_url = currentProject?.project?.id
    ? currentProject?.project?.id
    : null;
  const { data: projects } = useGetAllProjectsQuery();

  // Blog post list and functions
  const {
    data: blogPostList,
    isLoading,
    isError,
    refetch,
  } = useGetAllPostEditorBlogQuery(
    { website_url },
    {
      skip: isInitialized === false && currentProject?.project != null,
    }
  );

  const [deletePostEditor, { isLoading: deleteLoading }] =
    useDeleteSinglePostEditorBlogMutation();
  const [deleteSelectedPostEditor, { isLoading: deleteSelectedLoading }] =
    useDeleteSelectedPostEditorBlogMutation();
  const [createPostEditor, { isLoading: createLoading }] =
    useCreatePostEditorBlogMutation();
  const [linkPostEditorToWebsite, { isLoading: linkingLoading }] =
    useLinkSinglePostEditorToWebsiteMutation();
  const [competitorAnalysis, { isLoading: analyzing }] =
    useGetCompetitorAnalysisMutation();

  const userInfo = useSelector((state) => state?.auth).userInfo;
  const accessToken = useSelector((state) => state?.auth).accessToken;
  const formik = useFormik({
    initialValues: {
      seoInput: "",
    },
  });
  // SEO Analysis state
  const [seoInput, setSeoInput] = useState("");
  const [analysisState, setAnalysisState] = useState(null);

  useEffect(() => {
    dispatch(setCompetitorAnalysis(null));
    if (isInitialized) {
      refetch();
    }
  }, [refetch, isInitialized]);

  const navigate = useNavigate();
  const isReadOnly = userInfo.payment_status === "pending";

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deletionType, setDeletionType] = useState("single");
  const [selectedPosts, setSelectedPosts] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");
  const [seoPopupOpen, setSeoPopupOpen] = useState(false);

  // Gestion dynamique du placement du menu ReactSelect
  const [menuPlacements, setMenuPlacements] = useState({});
  const selectRefs = useRef({});
  const handleMenuOpen = (blogPostId) => () => {
    const ref = selectRefs.current[blogPostId];
    if (ref) {
      const rect = ref.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuPlacements((prev) => ({
        ...prev,
        [blogPostId]: spaceBelow < 220 ? "top" : "auto",
      }));
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);

    const formattedDate = date
      .toISOString()
      .slice(0, 10)
      .split("-")
      .reverse()
      .join("/"); // Format: dd/mm/yyyy
    const formattedTime = date.toISOString().slice(11, 16); // Format: HH:mm

    return `${formattedDate} à ${formattedTime}`;
  };

  const sortedBlogPosts = blogPostList?.data
    ? [...blogPostList.data].sort((a, b) => {
        return (
          new Date(b.last_update_date).getTime() -
          new Date(a.last_update_date).getTime()
        );
      })
    : [];

  const isAllSelected =
    sortedBlogPosts &&
    sortedBlogPosts.length > 0 &&
    sortedBlogPosts.every((post) => selectedPosts.has(post.id));

  const handleDeleteModal = (deleteType, open = true, id = null) => {
    setDeletionType(deleteType);
    setSelectedId(id);
    if (open) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleSeoAnalysis = async () => {
    trackPlatformUsed("blog_post", "seo_analysis");

    // Token check: if both are 0, block
    if (
      (userInfo?.seo_analysis_tokens || 0) === 0 &&
      (userInfo?.seo_non_perishing || 0) === 0
    ) {
      toast.error(
        t(
          "no-seo-tokens",
          "No SEO analysis tokens remaining. Upgrade subscription or buy SEO token pack"
        )
      );
      return;
    }

    // Input validation (from Formik)
    if (!formik.values.seoInput.trim()) {
      toast.error(
        t("please-enter-seo-input", "Veuillez saisir une entrée SEO")
      );
      return;
    }

    try {
      setAnalysisState(t("creating-blog-post", "Creating blog post..."));

      // 1. First create a new blog post
      const blogRes = await createPostEditor(DefaultPostEditor).unwrap();

      if (blogRes?.status !== 201) {
        toast.error(t("error-creating-post", "Failed to create blog post"));
        return;
      }

      // Update tokens after blog creation
      dispatch(
        setCredentials({
          accessToken,
          userInfo: {
            ...userInfo,
            blog_posts_tokens: blogRes?.data?.blog_post_tokens,
          },
        })
      );

      const createdPostId = blogRes.data.id;

      // 2. Now run SEO analysis
      setAnalysisState(
        t("grabbing-competitors", "Récupération des concurrents...")
      );

      const payload = {
        post_id: createdPostId,
        seo_request: formik.values.seoInput.trim(),
        dummy: process.env.REACT_APP_ISDUMMY === "true",
      };

      const seoRes = await competitorAnalysis(payload).unwrap();

      if (seoRes?.status === 201) {
        dispatch(setCompetitorAnalysis({ data: seoRes?.data }));
        dispatch(
          setCredentials({
            accessToken,
            userInfo: {
              ...userInfo,
              seo_analysis_tokens: seoRes?.seo_analysis_tokens,
              seo_non_perishing: seoRes?.seo_non_perishing,
            },
          })
        );
        toast.info(t("seo-token-used", "1 SEO analysis token used"));

        // 3. Attach SEO analysis result to the created blog post
        const updatedPostData = {
          ...DefaultPostEditor,
          seo_request: formik.values.seoInput.trim(),
          selected_seo_analysis_id: seoRes?.data?.id,
        };

        // await createPostEditor(updatedPostData).unwrap();
      }

      // 4. Navigate to created post editor after all done
      navigate(`/post-editor/${createdPostId}`);
    } catch (err) {
      console.error("SEO Analysis error:", err);
      toast.error(t("error-retrieving-data", "Error while retrieving data"));
    } finally {
      setAnalysisState(null);
      setSeoPopupOpen(false);
      formik.resetForm();
    }
  };

  const handleDeleteSelectedBlogPosts = async () => {
    trackPlatformUsed("blog_post", "delete_bulk_in_db");

    try {
      const res = await deleteSelectedPostEditor(
        Array.from(selectedPosts)
      ).unwrap();
      setSelectedPosts(new Set());
      setOpen(false);
      refetch();
      toast.success(
        t(
          "deleted-selected-blog-posts",
          "Les articles sélectionnés ont bien été supprimés"
        )
      );
    } catch (error) {
      toast.error(
        t(
          "error-deleting-selected-blog-posts",
          "Une erreur a eu lieu lors de la suppression des articles"
        )
      );
    }
  };

  const handleDeleteBlogPost = async (id, modal = false) => {
    trackPlatformUsed("blog_post", "delete_in_db");

    try {
      const res = await deletePostEditor({ id }).unwrap();
      if (modal) {
        setOpen(false);
      }
      refetch();
      toast.success(
        t("deleted-blog-post", "L'article sélectionné a bien été supprimé")
      );
    } catch (error) {
      toast.error(
        t(
          "error-deleting-blog-post",
          "Une erreur a eu lieu lors de la suppression de l'article"
        )
      );
    }
  };

  const handleSkipToDraft = async () => {
    setSeoPopupOpen(false);
    try {
      const res = await createPostEditor(DefaultPostEditor).unwrap();
      if (res?.status === 201) {
        dispatch(
          setCredentials({
            accessToken,
            userInfo: {
              ...userInfo,
              seo_analysis_tokens: res?.data?.seo_analysis_tokens,
              seo_non_perishing: res?.data?.seo_non_perishing,
            },
          })
        );
        navigate(`/post-editor/${res.data.id}`);
      }
    } catch (err) {
      toast.error(t(err.data.detail));
    }
  };

  // const handleCreateBlogPost = async () => {
  //   trackPlatformUsed("blog_post", "create_in_db");

  //   if (userInfo?.blog_posts_tokens === 0 || !userInfo?.blog_posts_tokens) {
  //     toast.error(
  //       t(
  //         "Upgrade-subscription-to-get-more-tokens",
  //         "Upgrade subscription to get more tokens"
  //       )
  //     );
  //   } else {
  //     try {
  //       const res = await createPostEditor(DefaultPostEditor).unwrap();
  //       if (res?.status === 201) {
  //         dispatch(
  //           setCredentials({
  //             accessToken: accessToken,
  //             userInfo: {
  //               ...userInfo,
  //               blog_posts_tokens: res?.data?.blog_post_tokens,
  //             },
  //           })
  //         );
  //         navigate(`/post-editor/${res.data.id}`);
  //       }
  //     } catch (err) {
  //       toast.error(t(err.data.detail));
  //     }
  //   }
  // };

  // CHECKBOX AND BULK ACTIONS
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allPostIds = sortedBlogPosts.map((post) => post.id);
      setSelectedPosts(new Set(allPostIds));
    } else {
      setSelectedPosts(new Set());
    }
  };

  const handleCheckboxChange = (postId) => {
    setSelectedPosts((prevSelectedPosts) => {
      const newSelectedPosts = new Set(prevSelectedPosts);
      if (newSelectedPosts.has(postId)) {
        newSelectedPosts.delete(postId);
      } else {
        newSelectedPosts.add(postId);
      }
      return newSelectedPosts;
    });
  };

  const handleApplyAction = async () => {
    if (bulkAction === "delete") {
      handleDeleteModal("selected", true, null);
    }
  };

  // PROJECTS
  const handleProjectChange = async (selectedProject, blogPostId) => {
    try {
      const projectId = selectedProject?.value ? selectedProject.value : null;
      await linkPostEditorToWebsite({ projectId, blogPostId });
      refetch();
    } catch (error) {
      console.error("Error selecting project:", error);
    }
  };

  const projectOptions = [
    {
      value: null, // This will send `null` when selected
      label: (
        <div className="flex items-center gap-2">
          <div className="font-bold text-sm">
            {t("Unselect", "Désélectionner")}
          </div>
        </div>
      ),
    },
    ...(projects?.data?.map((project) => ({
      value: project.id,
      label: (
        <div className="flex items-center gap-2">
          <img src={project.favicon} alt={project.name} className="w-6 h-6" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{project.name}</div>
            <div
              className="text-xs text-gray-500 truncate overflow-hidden whitespace-nowrap"
              title={project.url} // Tooltip for full URL
            >
              {project.url}
            </div>
          </div>
        </div>
      ),
    })) || []), // Fallback to an empty array if projects or data is undefined
  ];

  // Pour centrer le favicon dans la valeur sélectionnée
  const CenteredSingleValue = (props) => (
    <components.SingleValue {...props}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {props.children}
      </div>
    </components.SingleValue>
  );

  // Ajout pour customiser ReactSelect
  const customStyles = {
    menu: (base) => ({
      ...base,
      maxHeight: "200px",
      minWidth: "200px",
      margin: 0,
      padding: 0,
      zIndex: 9999,
      marginTop: "6px", // Ajout d'un espace entre le bouton et le menu
      marginBottom: "8px", // Ajout pour buffer en bas et améliorer le placement auto
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      margin: 0,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      maxHeight: "200px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      padding: 0,
      margin: 0,
    }),
  };

  return (
    <div>
      <Helmet>
        <title>{t("blog-posts", "Articles de blog")} | BotBlogR</title>
        <meta
          name="description"
          content="Vision générale de votre compte et de vos activités."
        />
      </Helmet>

      <div className="px-2 pl-6 pr-6">
        <div className="flex items-center justify-between py-3">
          <div>
            <H1Header title={t("My-blog-posts", "Mes articles de blog")} />
            {currentProject?.project && (
              <div className="inline-flex gap-2">
                <img
                  src={currentProject?.project?.favicon}
                  alt={currentProject?.project?.name}
                  className="w-6 h-6"
                />
                <h2 className="font-semibold font-Roboto text-xl">
                  {currentProject?.project?.url}
                </h2>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-bold text-center">
              {t("available-tokens", "Available Tokens")} :{" "}
              <span className="text-md text-blue-200">
                {(userInfo?.seo_analysis_tokens || 0) +
                  (userInfo?.seo_non_perishing || 0)}
              </span>
            </p>
            <BotBlogrButton
              onClick={() => setSeoPopupOpen(true)}
              disabled={isReadOnly}
              variant="blue100"
              className="rounded font-light"
            >
              {t("create-new-blog-post", "Créer un nouvel article de blog")}
            </BotBlogrButton>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 p-4">
        <div className="mx-auto bg-white shadow-md rounded-md overflow-hidden">
          <div className="p-4">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500">
              <thead className="text-xs text-white uppercase bg-blue-100">
                <tr>
                  {[
                    "",
                    t("Website", "Site"),
                    t("Title", "Titre"),
                    t("Target-Request", "Target Request"),
                    t("Score", "Score"),
                    t("Date", "Date"),
                  ].map((heading, index) => (
                    <th
                      key={index}
                      className={`[&:nth-child(3)]:w-[45%] px-6 py-3 ${
                        index === 0
                          ? "rounded-s-lg"
                          : index === 5
                          ? "rounded-e-lg"
                          : ""
                      } text-base col`}
                    >
                      {index === 0 ? (
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                      ) : (
                        heading
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedBlogPosts?.map((blogPost, index) => (
                  <tr
                    key={blogPost.id}
                    className="odd:bg-white even:bg-black/10 group transition-all"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words">
                      <input
                        type="checkbox"
                        checked={selectedPosts.has(blogPost.id)}
                        onChange={() => handleCheckboxChange(blogPost.id)}
                      />
                    </td>

                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words text-center">
                      <div
                        ref={(el) => {
                          selectRefs.current[blogPost.id] = el;
                        }}
                      >
                        <ReactSelect
                          options={projectOptions}
                          value={
                            blogPost.website && projects?.data
                              ? {
                                  value: projects.data.find(
                                    (obj) => obj.id === blogPost.website
                                  ).id,
                                  label: (
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={
                                          projects.data.find(
                                            (obj) => obj.id === blogPost.website
                                          ).favicon
                                        }
                                        alt={
                                          projects.data.find(
                                            (obj) => obj.id === blogPost.website
                                          ).name
                                        }
                                        className="w-6 h-6"
                                      />
                                    </div>
                                  ),
                                }
                              : null
                          }
                          styles={customStyles}
                          components={{
                            IndicatorSeparator: NoSeparator,
                            SingleValue: CenteredSingleValue,
                          }}
                          menuPlacement={menuPlacements[blogPost.id] || "auto"}
                          menuPosition="fixed"
                          menuPortalTarget={document.body}
                          onMenuOpen={handleMenuOpen(blogPost.id)}
                          onChange={(selectedOption) =>
                            handleProjectChange(selectedOption, blogPost.id)
                          }
                          isSearchable={false}
                          placeholder={"Aucun"}
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 break-words min-w-[50%] relative">
                      <div className="flex flex-col gap-2">
                        {blogPost.h1_title
                          ? blogPost.h1_title
                          : "(Pas de titre)"}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-4 text-blue-100 text-xs">
                          <Tooltip
                            title={
                              !userInfo?.plan &&
                              blogPost.blog_status === "draft"
                                ? t(
                                    "draft-edit-subscription-required",
                                    "Edit draft post is only available for users with a subscription"
                                  )
                                : ""
                            }
                            disableHoverListener={
                              !!userInfo?.plan ||
                              blogPost.blog_status !== "draft"
                            }
                          >
                            <span>
                              <BotBlogrButton
                                onClick={() => {
                                  trackPlatformUsed("blog_post", "edit");
                                  navigate(`/post-editor/${blogPost.id}`);
                                }}
                                disabled={
                                  !userInfo?.plan &&
                                  blogPost.blog_status === "draft"
                                }
                                className="hover:underline flex items-center gap-1 font-semibold disabled:text-black/40 disabled:no-underline disabled:cursor-not-allowed"
                                variant="transparent"
                                icon={EditIcon}
                              >
                                {t("Edit", "Modifier")}
                              </BotBlogrButton>
                            </span>
                          </Tooltip>

                          <BotBlogrButton
                            onClick={() =>
                              handleDeleteModal("single", true, blogPost.id)
                            }
                            className="hover:underline flex items-center gap-1 text-red-500 font-semibold"
                            variant="transparent"
                            icon={DeleteIcon}
                          >
                            {t("Delete", "Supprimer")}
                          </BotBlogrButton>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words whitespace-pre-wrap break-normal">
                      {blogPost.seo_request}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <CircularGauge
                        percentage={blogPost.seo_score ? blogPost.seo_score : 0}
                        isEvenRow={index % 2 !== 0}
                      />
                    </td>

                    <td className="px-6 py-4 text-xs font-medium text-gray-900 break-words">
                      {blogPost.creation_date === blogPost.last_update_date
                        ? "Créé le"
                        : "Dernière modification le"}
                      <br />
                      {formatDate(blogPost.last_update_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex space-x-4 mt-4">
              <select
                className="border rounded p-2 bg-white"
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <option value="">{t("bulk-action", "Actions groupées")}</option>
                <option value="delete">{t("delete", "Supprimer")}</option>
              </select>
              <BotBlogrButton
                onClick={handleApplyAction}
                disabled={selectedPosts.size === 0}
                variant="blue100"
                className="disabled:bg-slate-200 disabled:text-slate-400 rounded font-light"
              >
                {t("apply", "Appliquer")}
              </BotBlogrButton>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={() => handleDeleteModal("single", false, null)}
        className="relative z-[999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-error"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {deletionType === "single"
                        ? t("delete-blog-post", "Supprimer l'article de blog")
                        : t(
                            "delete-selected-blog-post",
                            "Supprimer les articles de blog sélectionnés"
                          )}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  {deletionType === "single"
                    ? "Êtes-vous sûr de vouloir supprimer votre article de blog ? Les données seront effacées et vous ne pourrez plus les récupérer."
                    : "Êtes-vous sûr de vouloir supprimer ces articles de blog ? Les données seront effacées et vous ne pourrez plus les récupérer."}
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <BotBlogrButton
                    type="button"
                    onClick={
                      deletionType === "single"
                        ? () => handleDeleteBlogPost(selectedId, true)
                        : () => handleDeleteSelectedBlogPosts()
                    }
                    variant="dangerSolid"
                    className="w-full sm:ml-3 sm:w-auto"
                  >
                    Supprimer
                  </BotBlogrButton>

                  <BotBlogrButton
                    type="button"
                    data-autofocus
                    onClick={() => handleDeleteModal("single", false, null)}
                    variant="cancel"
                    className="w-full mt-3 sm:mt-0 sm:w-auto"
                  >
                    Annuler
                  </BotBlogrButton>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* SEO Analysis Dialog */}
      <Dialog
        open={seoPopupOpen}
        onClose={() => setSeoPopupOpen(false)}
        className="relative z-[999] md:w-[800px] xl:w-[900px]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all
             data-[closed]:translate-y-4 data-[closed]:opacity-0 
             data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in
             w-full max-w-md md:max-w-[500px] sm:p-6"
            >
              {/* Cross button in top-right */}
              <BotBlogrButton
                onClick={() => {
                  formik.resetForm();
                  setSeoPopupOpen(false);
                }}
                disabled={analyzing || analysisState}
                variant="icon"
                className="absolute right-3 top-3"
              >
                <RxCross2 className="h-5 w-5 text-gray-600" />
              </BotBlogrButton>

              <DialogTitle
                as="h3"
                className="text-lg font-semibold leading-6 text-gray-900"
              >
                {t("seo-analysis", "SEO Analysis")}
              </DialogTitle>

              <p className="mt-4 mb-0">
                {t("SEO-target-request", "Requête SEO cible")}
                <span className="text-red-500 ml-1">*</span>
              </p>
              <GlobalTextField
                formikProps={formik}
                name="seoInput"
                placeholder={t("enter-seo-keyword", "Enter SEO keyword")}
                maxLength={100}
                multipleLines={false}
                isRequired={true}
              />

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                {!analysisState ? (
                  !analyzing ? (
                    <Tooltip
                      title={
                        (userInfo?.seo_analysis_tokens || 0) +
                          (userInfo?.seo_non_perishing || 0) ===
                        0
                          ? t(
                              "seo-no-tokens-left",
                              "You don’t have any tokens left. Please purchase tokens to continue."
                            )
                          : !formik.values.seoInput.trim()
                          ? t(
                              "seo-need-input",
                              "Fill the target SEO query input"
                            )
                          : ""
                      }
                      disableHoverListener={
                        !(
                          (userInfo?.seo_analysis_tokens || 0) +
                            (userInfo?.seo_non_perishing || 0) ===
                            0 || !formik.values.seoInput.trim()
                        )
                      }
                    >
                      <span className="w-full flex">
                        <LoadingButton
                          startIcon={<MdOutlineQueryStats />}
                          onClick={handleSeoAnalysis}
                          variant="contained"
                          loading={analyzing}
                          className="!bg-pink-600 hover:!bg-pink-700 text-white disabled:!bg-slate-200 disabled:!text-slate-400 font-light px-5 py-[6px] rounded flex items-center w-full justify-center"
                          disabled={
                            !formik.values.seoInput.trim() ||
                            (userInfo?.seo_analysis_tokens || 0) +
                              (userInfo?.seo_non_perishing || 0) ===
                              0
                          }
                          sx={{
                            textTransform: "none",
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: 300,
                            fontSize: "16px",
                          }}
                        >
                          {t("Launch-analysis", "Lancer l'analyse SEO")}
                        </LoadingButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <button
                      type="button"
                      className="gap-2 text-sm !bg-pink-600 hover:!bg-pink-700 text-white disabled:bg-slate-200 disabled:text-slate-100 font-light px-5 py-[6px] rounded flex items-center w-[100%]"
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 300,
                        fontSize: "16px",
                      }}
                      disabled
                    >
                      <div className="w-5 h-5 border-2 border-slate-100 border-t-transparent rounded-full animate-spin"></div>
                      {t(
                        "grabbing-competitors",
                        "Récupération des concurrents..."
                      )}
                    </button>
                  )
                ) : (
                  <button
                    type="button"
                    className="gap-2 !bg-pink-600 hover:!bg-pink-700 text-white 
             disabled:bg-slate-200 disabled:text-slate-100 font-light 
             px-5 py-[6px] rounded flex items-center justify-center w-full"
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 300,
                      fontSize: "16px",
                    }}
                    disabled
                  >
                    <div className="w-5 h-5 border-2 border-slate-100 border-t-transparent rounded-full animate-spin shrink-0 mt-0.5"></div>
                    <span className="text-left break-words">
                      {analysisState}
                    </span>
                  </button>
                )}
                <Tooltip
                  title={
                    !userInfo?.plan
                      ? t(
                          "draft-need-subscription",
                          "Creating a draft is only available for users with a subscription"
                        )
                      : ""
                  }
                  disableHoverListener={!!userInfo?.plan}
                >
                  <span className="w-full flex">
                    <BotBlogrButton
                      onClick={handleSkipToDraft}
                      disabled={analyzing || analysisState || !userInfo?.plan}
                      variant="blue"
                      className="rounded font-light mt-3 md:mt-0 w-full flex justify-center disabled:!bg-slate-200 disabled:!text-slate-400"
                    >
                      {t("skip-and-create-draft", "Skip and Create Draft")}
                    </BotBlogrButton>
                  </span>
                </Tooltip>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default AllPostEditor;
